import * as MailComposer from 'expo-mail-composer';
import { router, useNavigation } from 'expo-router';
import { useLayoutEffect, useState } from 'react';
import { Linking, Platform, StyleSheet, View } from 'react-native';
import { Divider } from 'react-native-paper';
import Animated, { FadeOutUp, SlideInDown } from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';
import Toast from 'react-native-toast-message';

import BrandedButton from '@/components/buttons/BrandedButton';
import GoBackButton from '@/components/buttons/GoBackButton';
import ClaimNpoForm from '@/components/forms/ClaimNpoForm';
import SearchNpoAutoComplete from '@/components/inputs/SearchNpoAutoComplete';
import { PredictionType } from '@/components/inputs/TextIputAutocomplete';
import Text from '@/components/Text';
import Title from '@/components/Title';
import { BS_CONTACT_EMAIL } from '@/constants/config';
import { useAuth } from '@/contexts/authContext';
import { i18n } from '@/utils/i18n';

const FindNpo = () => {
  const navigation = useNavigation();

  const [result, setResult] = useState<PredictionType | undefined>(undefined);
  const [hasSearched, setHasSearched] = useState(false);
  const [hasClaimed, setHasClaimed] = useState(false);

  const { state } = useAuth();

  const npoUserNotLogged = state.authReady && state.user?.user.role !== 'NON_PROFIT_ORG';

  useLayoutEffect(() => {
    const findNpoHeader = {
      headerShown: true,
      headerShadowVisible: false,
      headerBackVisible: false,
      headerTitle: () => <Title label="" />,
      headerLeft: () => <GoBackButton />
    };

    navigation.setOptions(
      npoUserNotLogged && {
        ...findNpoHeader
      }
    );
  }, [navigation, npoUserNotLogged]);

  const results = () => {
    return (
      <Animated.View style={styles.flexOne} entering={SlideInDown} exiting={FadeOutUp}>
        {!hasClaimed && (
          <>
            <Text fontWeight="500" style={styles.labelText}>
              {i18n.t('mix.results')}
            </Text>
            <Text fontWeight="500" style={styles.result}>
              {result?.label}
            </Text>
            <Divider style={styles.toClaim} />
          </>
        )}

        <ClaimNpoForm
          npoName={result?.label ?? '-'}
          nonProfitOrgId={result?.value ?? '-'}
          onClaimed={() => setHasClaimed(true)}
        />
      </Animated.View>
    );
  };

  const noResults = () => {
    return (
      <Animated.View style={styles.flexOne} entering={SlideInDown} exiting={FadeOutUp}>
        <Text style={styles.labelText}>{i18n.t('mix.results')}</Text>
        <Text fontWeight="500" style={styles.result}>
          {i18n.t('claim_npo.no_results')}
        </Text>
        <Text style={[styles.labelText, styles.errorContactUs]}>{i18n.t('claim_npo.error_contact_us')}</Text>
        <View style={styles.contactBrowse}>
          <BrandedButton
            onPress={() => {
              // ToDO: Change for actual support email data
              if (Platform.OS === 'web') {
                Linking.openURL(`mailto:${BS_CONTACT_EMAIL}?subject=NPO Not Found&body=I didn't find an NPO`);
              } else {
                try {
                  MailComposer.composeAsync({
                    recipients: [BS_CONTACT_EMAIL],
                    subject: `NPO Not Found`,
                    body: `I didn't find an NPO`
                  });
                } catch (e) {
                  console.error(e);
                  Toast.show({
                    type: 'error',
                    text1: i18n.t('mix.mail_not_openned.title'),
                    text2: i18n.t('mix.mail_not_openned.message'),
                    position: 'bottom',
                    visibilityTime: 3500
                  });
                }
              }
            }}>
            {i18n.t('claim_npo.contact_bs')}
          </BrandedButton>
          <BrandedButton onPress={() => router.replace('/home/npoTabs')}>{i18n.t('claim_npo.browse_bs')}</BrandedButton>
        </View>
      </Animated.View>
    );
  };

  return (
    <SafeAreaView
      style={styles.flexOne}
      {...(npoUserNotLogged && {
        edges: ['bottom']
      })}>
      <View style={styles.mainContainer}>
        {!hasClaimed && (
          <>
            <Text fontWeight="700" style={styles.boldTitle}>
              {i18n.t('claim_npo.find_your_npo')}
            </Text>
            <Text fontWeight="450" style={[styles.labelText, styles.searchNpo]}>
              {i18n.t('claim_npo.search_your_npo')}
            </Text>
            <SearchNpoAutoComplete
              claimed={false}
              placeholder={i18n.t('claim_npo.search_npo_placeholder')}
              showSearch
              onSearch={() => {
                setHasSearched(true);
              }}
              onNoResults={() => {
                setResult(undefined);
              }}
              onPredictionTap={({ value, label }) => {
                setHasSearched(true);
                setResult({ value: value, label: label });
              }}
            />
          </>
        )}
        {hasSearched && <>{result ? results() : noResults()}</>}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  mainContainer: {
    flex: 1,
    flexDirection: 'column',
    paddingHorizontal: 18
  },

  boldTitle: {
    fontSize: 22,
    color: 'black'
  },

  labelText: {
    color: 'rgba(0, 0, 0, 0.60)',
    fontSize: 16
  },

  result: {
    color: '#1A951C',
    fontSize: 16,
    marginTop: 10
  },

  searchNpo: {
    marginTop: 8,
    marginBottom: 25
  },

  browseMeanwhile: {
    marginTop: 15,
    marginBottom: 25
  },

  errorContactUs: {
    marginTop: 40,
    marginBottom: 15
  },

  contactBrowse: {
    gap: 25
  },

  phoneEmail: {
    gap: 15,
    marginBottom: 25
  },

  contactYou: {
    marginTop: 10,
    marginBottom: 15
  },

  toClaim: {
    marginVertical: 25
  }
});

export default FindNpo;
